import type { TCountryCode } from "countries-list";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import ISO6391 from "iso-639-1";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useIsMounted } from "usehooks-ts";
import AddToCartToaster from "../../components/AddToCartToaster/AddToCartToaster";
import AlertBanner from "../../components/general/AlertBanner";
import { getFlag } from "../../components/general/Flag/Flag";
import GridContainer from "../../components/general/GridContainer";
import Header from "../../components/general/Header";
import ImageContainer from "../../components/general/ImageContainers/ImageContainer";
import ListItem from "../../components/general/List/components/ListItem/ListItem";
import Modal from "../../components/general/Modal";
import ModalOverlay from "../../components/general/Modal/components/ModalOverlay/ModalOverlay";
import SidebarSiteSelectionListItemItem from "../../components/general/Modal/components/SidebarSiteSelectionListItem";
import ModalPortal from "../../components/general/ModalPortal";
import Navigation from "../../components/general/Navigation";
import GlobalLink from "../../components/general/Navigation/components/GlobalLink";
import GlobalLinks from "../../components/general/Navigation/components/GlobalLinks";
import MenuList from "../../components/general/Navigation/components/MenuList";
import NavButton from "../../components/general/Navigation/components/NavButton";
import SiteMenu from "../../components/general/SiteMenu";
import TopBar from "../../components/general/TopBar";
import { AppDispatchContext, useAppState } from "../../context/app/context";
import { useCartProducts } from "../../hooks/use-cart-products";
import { useDispatchContext } from "../../hooks/use-dispatch-context";
import { useTranslatedPaths } from "../../hooks/use-translated-paths";
import { pushCustomGTMEvent } from "../../utils/gtm/gtm-handlers";
import { getUrlFromStoryblokLink } from "../../utils/storyblok/urls";
import CartContainer from "./cart.container";

type SiteHeaderContainerProps = {
	showCart?: boolean;
	showQuickSearch?: boolean;
};

const SiteHeaderContainer: FC<SiteHeaderContainerProps> = ({
	showCart = true,
	showQuickSearch = true,
}) => {
	const {
		siteConfig,
		hideNoticeBar,
		userCountry,
		allowedCountries,
		searchOpen,
		drawerIndices,
		navOpen,
	} = useAppState();

	const { storesPath } = useTranslatedPaths();

	const appDispatch = useDispatchContext(AppDispatchContext);

	const [languageCode] = siteConfig.language.split("-");

	const language = ISO6391.getNativeName(languageCode ?? "");
	const { itemsInCart } = useCartProducts();

	const data = useStaticQuery<Queries.HeaderQuery>(query);
	const emptyCartData = data.navigation?.empty_cart_modal?.[0];
	const empytCartButton = emptyCartData?.button?.[0];

	const emptyCartContentProps = {
		title: emptyCartData?.title ?? "",
		description: emptyCartData?.description ?? "",
		backgroundImage: (
			<ImageContainer
				alt={emptyCartData?.backgroundImage?.alt ?? undefined}
				objectFit="cover"
				imgSrc={emptyCartData?.backgroundImage?.filename ?? ""}
				fullHeight
			/>
		),
		bannerIcon: emptyCartData?.bannerIcon ?? "",
		bannerText: emptyCartData?.bannerText ?? "",
		buttonHref: getUrlFromStoryblokLink(empytCartButton?.link),
		buttonIcon: empytCartButton?.icon ?? undefined,
		buttonLabel: empytCartButton?.label ?? "",
	};

	const { t } = useTranslation();

	const [modal, setModal] = useState<"cart" | "language" | undefined>();

	const openModal = (modal: string) => {
		if (modal === "cart") {
			setModal("cart");
		}
		if (modal === "language") {
			setModal("language");
		}
	};

	const closeModal = () => {
		setModal(undefined);
	};

	useEffect(() => {
		const close = (e: { key: string }) => {
			if (modal !== undefined && e.key === "Escape") {
				setModal(undefined);
			}
		};
		window.addEventListener("keydown", close);
		return () => window.removeEventListener("keydown", close);
	}, [modal, setModal]);

	const showAllLabel = t("Show all");

	const headerRef = useRef<HTMLElement | null>(null);

	const [headerHeight, setHeaderHeight] = useState(0);
	const isMounted = useIsMounted();

	useEffect(() => {
		if (isMounted()) {
			setHeaderHeight(headerRef.current?.clientHeight || 0);
		}

		const currentHeader = headerRef.current;

		const handleResize = () => {
			if (currentHeader) {
				setHeaderHeight(currentHeader.clientHeight);
			}
		};

		window.addEventListener("resize", () => handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [isMounted]);

	const informationSliderItems = (
		data.navigation?.information_slider || []
	).map((item) => ({
		_uid: item?._uid || "",
		icon: item?.icon || "",
		text: item?.text || "",
		href: getUrlFromStoryblokLink(item?.link),
	}));

	const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

	const setCountryAndCloseModal = useCallback(
		(country: TCountryCode) => {
			appDispatch({ type: "setUserCountry", userCountry: country });
			setIsLanguageModalOpen(false);
		},
		[appDispatch],
	);

	const closeAlertBanner = () => {
		appDispatch({ type: "hideNoticeBar", hideNoticeBar: true });
		setTimeout(() => {
			if (headerRef.current) {
				setHeaderHeight(headerRef.current.clientHeight);
			}
		}, 0);

		window.localStorage.setItem("alertClosed", JSON.stringify(Date.now()));
	};

	const [[openDrawerIndex, prevOpenDrawerIndex], setDrawerIndices] = useState<
		(number | null | undefined)[]
	>([null, null]);

	const closeNav = useCallback(() => {
		appDispatch({
			type: "setNavOpen",
			navOpen: false,
		});
		appDispatch({
			type: "setDrawerIndices",
			drawerIndices: [null, null],
		});
	}, [appDispatch]);

	const openNav = useCallback(() => {
		if (!navOpen) {
			appDispatch({
				type: "setNavOpen",
				navOpen: true,
			});
		}
	}, [appDispatch, navOpen]);

	const onFlagClick = useCallback(() => {
		setIsLanguageModalOpen(true);
		closeNav();
	}, [closeNav]);

	useEffect(() => {
		closeNav();
	}, [closeNav]);

	useEffect(() => {
		const alertClosedTime = window.localStorage.getItem("alertClosed");

		if (alertClosedTime) {
			const currentTime = Date.now();

			// Dont show alert if it has been closed in the last 24 hours
			if (currentTime - Number.parseInt(alertClosedTime) < 86400000) {
				appDispatch({ type: "hideNoticeBar", hideNoticeBar: true });
			}
		}
	}, [appDispatch]);

	useEffect(() => {
		appDispatch({
			type: "setDrawerIndices",
			drawerIndices: [openDrawerIndex, prevOpenDrawerIndex],
		});
	}, [appDispatch, openDrawerIndex, prevOpenDrawerIndex]);

	const onCloseModal = () => {
		if (searchOpen) {
			appDispatch({
				type: "setSearchOpen",
				searchOpen: false,
			});
		}
		closeNav();
	};
	return (
		<GridContainer className="!mb-2">
			<Header
				isOpen={searchOpen || navOpen || drawerIndices[0] !== null}
				ref={headerRef}
				onCloseNav={onCloseModal}
			>
				{!hideNoticeBar && data.navigation?.alert_message && (
					<AlertBanner onClick={closeAlertBanner} ariaLabelClose={t("close")}>
						{data.navigation.alert_message}
					</AlertBanner>
				)}
				<TopBar
					information={informationSliderItems}
					flagAriaLabel={t("Flag for")}
					openLanguageModal={onFlagClick}
					helpCenter={{
						label: t("Help center"),
						link: {
							href: getUrlFromStoryblokLink(data.navigation?.help_center),
							target: data.navigation?.help_center?.target ?? undefined,
						},
					}}
					findStore={{
						label: t("Find store"),
						link: storesPath,
					}}
					userCountry={userCountry}
					allowedCountries={allowedCountries}
				/>
				<SiteMenu
					showCart={showCart}
					showQuickSearch={showQuickSearch}
					cartItemCount={itemsInCart}
					onCartClick={() => {
						openModal("cart");
						pushCustomGTMEvent({
							type: "cartPreview",
						});
						closeNav();
					}}
					onNavClick={openNav}
					ariaLabelMenu={t("Menu")}
					ariaLabelCartItems={t("Cart Items")}
					ariaLabelLogo={t("Link")}
				/>
				<AddToCartToaster cartModalOpen={modal === "cart"} />
				<Navigation
					onClose={closeNav}
					closeButtonLabel={t("Close")}
					isOpen={navOpen}
				>
					<MenuList>
						{data.navigation?.menu_items?.map((item, i) => (
							<NavButton
								key={item?._uid}
								title={item?.title || ""}
								image={
									item?.image?.filename && (
										<ImageContainer
											ratio="square"
											objectFit="cover"
											sizes="72px"
											width={144}
											imgSrc={item.image.filename}
											alt={item.image.alt ?? undefined}
										/>
									)
								}
								href={getUrlFromStoryblokLink(item?.link)}
								openDrawerIndex={drawerIndices[0]}
								onClick={() =>
									setDrawerIndices((prev) => [
										i === openDrawerIndex ? null : i,
										prev[0],
									])
								}
								index={i}
								previousOpenDrawerIndex={drawerIndices[1]}
								headerHeight={headerHeight}
							>
								{item?.submenus && drawerIndices[0] === i && (
									<Navigation
										isOpen={drawerIndices[0] === i}
										isSubNav
										onClose={() => {
											setDrawerIndices([null, null]);
										}}
										closeButtonLabel={t("Back")}
									>
										{item.submenus.map((submenu) => (
											<MenuList
												key={submenu?._uid}
												title={submenu?.title || ""}
												isSubMenuList
												showAllLabel={showAllLabel}
												showAllHref={getUrlFromStoryblokLink(
													submenu?.showAllLink,
												)}
											>
												{submenu?.submenu_items?.map((subItem) => (
													<ListItem
														key={subItem?._uid}
														title={subItem?.title || ""}
														description={subItem?.description || ""}
														tag={subItem?.tag || ""}
														href={getUrlFromStoryblokLink(subItem?.link)}
														target={subItem?.link?.target || "_self"}
														image={
															subItem?.image?.filename && (
																<ImageContainer
																	ratio={
																		subItem.variant === "block"
																			? "landscape"
																			: "square"
																	}
																	sizes={
																		subItem.variant === "block"
																			? "(max-width: 990px) 600px, 400px"
																			: "72px"
																	}
																	width={
																		subItem.variant === "block"
																			? undefined
																			: 144
																	}
																	objectFit="cover"
																	imgSrc={subItem.image.filename}
																	alt={subItem.image.alt ?? undefined}
																	loading="eager"
																/>
															)
														}
														variant={subItem?.variant || "standard"}
													/>
												))}
											</MenuList>
										))}
									</Navigation>
								)}
							</NavButton>
						))}
						<li>
							<GlobalLinks>
								<GlobalLink
									label={t("help center")}
									href={getUrlFromStoryblokLink(data.navigation?.help_center)}
									target={data.navigation?.help_center?.target ?? undefined}
									icon="message"
								/>
								<GlobalLink
									label={t("Find store")}
									href={storesPath}
									icon="pin"
								/>
								<GlobalLink
									label={language}
									onClick={() => {
										closeNav();
										setIsLanguageModalOpen(true);
									}}
									icon={getFlag(allowedCountries, userCountry)}
								/>
							</GlobalLinks>
						</li>
					</MenuList>
				</Navigation>
			</Header>
			<ModalPortal show={!!modal}>
				<AnimatePresence>
					{modal === "cart" && (
						<>
							<ModalOverlay />
							<motion.div
								initial={{ x: "100%" }}
								animate={{ x: 0 }}
								exit={{ x: "100%" }}
								transition={{ type: "spring", bounce: 0, duration: 0.4 }}
								style={{ position: "fixed", top: 0, right: 0, zIndex: 101 }}
							>
								<CartContainer
									closeModal={closeModal}
									emptyCartData={emptyCartContentProps}
								/>
							</motion.div>
						</>
					)}
				</AnimatePresence>
			</ModalPortal>
			<ModalPortal show={isLanguageModalOpen}>
				<AnimatePresence>
					{isLanguageModalOpen && (
						<>
							<ModalOverlay onClick={() => setIsLanguageModalOpen(false)} />
							<motion.div
								initial={{ x: "100%" }}
								animate={{ x: 0 }}
								exit={{ x: "100%" }}
								transition={{ type: "spring", bounce: 0, duration: 0.4 }}
								style={{ position: "fixed", top: 0, right: 0, zIndex: 101 }}
							>
								<Modal
									closeButtonLabel={t("Close")}
									overlayOnClick={() => setIsLanguageModalOpen(false)}
									buttonOnClick={() => setIsLanguageModalOpen(false)}
								>
									<SidebarSiteSelectionListItemItem
										setCountryAndCloseModal={setCountryAndCloseModal}
									/>
								</Modal>
							</motion.div>
						</>
					)}
				</AnimatePresence>
			</ModalPortal>
		</GridContainer>
	);
};

export default SiteHeaderContainer;

const query = graphql`
	query Header {
		siteConfigs: allParsedSiteConfig {
			nodes {
				countries
				domain
				language
			}
		}
		navigation {
			find_store {
				...Link
			}
			help_center {
				...Link
			}
			information_slider {
				_uid
				icon
				text
				link {
					...Link
				}
			}
			menu_items {
				_uid
				title
				link {
					...Link
				}
				image {
					...ImageAsset
				}
				submenus {
					_uid
					title
					showAllLink {
						...Link
					}
					submenu_items {
						_uid
						title
						description
						tag
						image {
							...ImageAsset
						}
						link {
							...Link
						}
						variant
					}
				}
			}
			alert_message
			empty_cart_modal {
				_uid
				component
				bannerIcon
				bannerText
				backgroundImage {
					...ImageAsset
				}
				title
				description
				button {
					...Button
				}
			}
		}
	}
`;
